import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Card,
  Button,
  Switch,
  Label,
} from "@b2b/ui-components";
import { Search } from "lucide-react";

type BaseFilter = {
  type: "select" | "input" | "button" | "switch";
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: (val: any) => void;
};

export type SelectFilter = BaseFilter & {
  type: "select";
  options: { label: string; value: string }[];
  icon?: React.ElementType;
};

export type InputFilter = BaseFilter & {
  type: "input";
  icon?: React.ElementType;
};

export type ButtonFilter = BaseFilter & {
  type: "button";
  label?: string;
  icon?: React.ElementType;
  variant?: string;
  size?: string;
};

export type SwitchFilter = BaseFilter & {
  type: "switch";
  label: string;
  checked?: boolean;
  onChange?: (val: boolean) => void;
};

export type FilterConfig =
  | SelectFilter
  | InputFilter
  | ButtonFilter
  | SwitchFilter;

type FiltersBarProps = {
  filters: FilterConfig[];
  showSearch?: boolean;
  card?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
};

export function FiltersBar({
  filters,
  showSearch = false,
  card = true,
  searchValue = "",
  onSearchChange,
}: FiltersBarProps) {
  const Wrapper = card ? Card : "div";

  return (
    <Wrapper className={card ? "bg-white border shadow-sm rounded-lg" : ""}>
      <div className="p-4 overflow-x-auto">
        <div className="flex flex-row items-center gap-3 whitespace-nowrap">
          {showSearch && (
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                className="pl-10 w-full"
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
              />
            </div>
          )}

          {/* Dynamic Filters */}
          {filters.map((filter, idx) => {
            if (filter.type === "input") {
              const Icon = filter.icon;
              return (
                <div key={idx} className="relative min-w-[160px] flex-1">
                  {Icon && (
                    <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  )}
                  <Input
                    placeholder={filter.placeholder}
                    className={`pl-9 w-full ${filter.className || ""}`}
                    value={filter.value}
                    onChange={(e) => filter.onChange?.(e.target.value)}
                  />
                </div>
              );
            }

            if (filter.type === "select") {
              const Icon = filter.icon;
              return (
                <Select
                  key={idx}
                  value={filter.value}
                  onValueChange={(val) => filter.onChange?.(val)}
                >
                  <SelectTrigger
                    className={`min-w-[140px] flex-1 ${filter.className || ""}`}
                  >
                    {Icon && <Icon className="w-4 h-4 mr-2" />}
                    <SelectValue placeholder={filter.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {filter.options.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        className="bg-white hover:bg-orange-100 outline-none"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }

            if (filter.type === "button") {
              const { icon: Icon, label, variant, size } = filter;
              return (
                <Button
                  key={idx}
                  variant={variant as any}
                  size={size as any}
                  className="min-w-[40px] flex-1"
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {label && (
                    <span className="hidden lg:inline ml-1">{label}</span>
                  )}
                </Button>
              );
            }

            if (filter.type === "switch") {
              return (
                <div
                  key={idx}
                  className="flex items-center space-x-2 min-w-[160px]"
                >
                  <Switch
                    id={`switch-${idx}`}
                    checked={filter.checked}
                    onCheckedChange={(val) => filter.onChange?.(val)}
                  />
                  <Label
                    htmlFor={`switch-${idx}`}
                    className="text-sm font-medium whitespace-nowrap text-gray-500"
                  >
                    {filter.label}
                  </Label>
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </Wrapper>
  );
}
